import { ArrowLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CryodPivotTable, type DrillPin } from "@/components/ci/cryod-pivot-table";
import { KpiTile, PillGroup } from "@/components/ci/overview-cells";
import { CiStatusBadge } from "@/components/ci/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTimezone } from "@/contexts/timezone-context";
import { useCryodFacets, useCryodOverview, useCryodRuns, useCryodSummary } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSearchParamSetter } from "@/hooks/use-search-param";
import {
  CRYOD_AGGS,
  CRYOD_FIXED_DIMENSIONS,
  CRYOD_METRICS,
  CRYOD_WINDOWS,
  cryodDimensionLabel,
  cryodValueLabel,
} from "@/lib/ci-filters";
import { buildCryodDetailHref, parseCryodFilters, pinsToFilterParams } from "@/lib/cryod-detail";
import type { CryodSummary } from "@/lib/types";
import { formatBytes, formatMetricValue, formatMs } from "@/lib/utils";

function passRateTone(rate: number | null): string {
  if (rate == null) return "";
  if (rate >= 0.95) return "text-emerald-600 dark:text-emerald-400";
  if (rate >= 0.8) return "text-amber-600 dark:text-amber-400";
  return "text-rose-600 dark:text-rose-400";
}

function Kpis({ summary }: { summary: CryodSummary | undefined }) {
  const passRate = summary?.pass_rate ?? null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <KpiTile label="Runs" value={summary ? String(summary.run_count) : "—"} />
      <KpiTile
        label="Pass rate"
        value={passRate == null ? "—" : `${(passRate * 100).toFixed(passRate < 1 ? 1 : 0)}%`}
        tone={passRateTone(passRate)}
      />
      <KpiTile label="Restore p50" value={formatMs(summary?.restore_p50 ?? null)} />
      <KpiTile label="Restore p95" value={formatMs(summary?.restore_p95 ?? null)} />
      <KpiTile
        label="Time saved"
        value={formatMs(summary?.time_saved_ms ?? null)}
        tone="text-emerald-600 dark:text-emerald-400"
      />
      <KpiTile label="Checkpoint size" value={formatBytes(summary?.checkpoint_size_p50 ?? null)} />
    </div>
  );
}

export function CiOverviewDetailPage() {
  usePageTitle("Cryod Overview");
  const navigate = useNavigate();
  const { formatTimestamp } = useTimezone();
  const [searchParams] = useSearchParams();
  const setView = useSearchParamSetter();

  const pins = useMemo(() => parseCryodFilters(searchParams), [searchParams]);
  const pinStrings = useMemo(() => pinsToFilterParams(pins), [pins]);
  const metric = searchParams.get("metric") ?? "total_restore_ms";
  const agg = searchParams.get("agg") ?? "p50";
  const window = searchParams.get("window") ?? "30d";

  const facets = useCryodFacets().data;
  const dimensionOptions = useMemo(() => {
    const extras = (facets?.extra_tag_keys ?? []).map((key) => ({
      label: cryodDimensionLabel(`extra:${key}`),
      value: `extra:${key}`,
    }));
    return [...CRYOD_FIXED_DIMENSIONS, ...extras];
  }, [facets]);

  // Break down by a dimension that isn't already pinned for this slice.
  const pinnedDims = useMemo(() => new Set(pins.map((pin) => pin.dimension)), [pins]);
  const breakdownOptions = useMemo(
    () => dimensionOptions.filter((option) => !pinnedDims.has(option.value)),
    [dimensionOptions, pinnedDims],
  );
  const breakdownParam = searchParams.get("breakdown") ?? "";
  const breakdown =
    breakdownParam && !pinnedDims.has(breakdownParam)
      ? breakdownParam
      : (breakdownOptions[0]?.value ?? "model_id");

  const summary = useCryodSummary({ window, filters: pinStrings }).data;

  const overviewQuery = useCryodOverview({
    group_by: breakdown,
    metric,
    agg,
    window,
    filters: pinStrings,
  });

  const runsQuery = useCryodRuns({ window, metric, filters: pinStrings, limit: 50 });

  const metricDef = CRYOD_METRICS.find((entry) => entry.value === metric);
  const unit = metricDef?.unit ?? "ms";
  const metricLabel = metricDef?.label ?? metric;

  const handleDrill = useCallback(
    (drillPins: DrillPin[]) => {
      navigate(
        buildCryodDetailHref({
          filters: pinStrings,
          pins: drillPins,
          view: { metric, agg, window },
        }),
      );
    },
    [navigate, pinStrings, metric, agg, window],
  );

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 p-6">
      {/* Breadcrumb — each segment links to that level of the drill. */}
      <div className="flex flex-wrap items-center gap-1 text-sm">
        <Link
          to="/ci/overview"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Overview
        </Link>
        {pins.map((pin, index) => {
          const isLast = index === pins.length - 1;
          const label = cryodValueLabel(pin.dimension, pin.value);
          return (
            <span key={`${pin.dimension}=${pin.value}`} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
              {isLast ? (
                <span className="font-medium">{label}</span>
              ) : (
                <Link
                  to={buildCryodDetailHref({
                    filters: [],
                    pins: pins.slice(0, index + 1),
                    view: { metric, agg, window },
                  })}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {label}
                </Link>
              )}
            </span>
          );
        })}
      </div>

      <div>
        <h1 className="text-2xl font-semibold">
          {pins.map((pin) => cryodValueLabel(pin.dimension, pin.value)).join(" · ") || "Slice"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {pins.map((pin) => cryodDimensionLabel(pin.dimension)).join(" · ")}
        </p>
      </div>

      {/* View controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Metric
          </span>
          <Select value={metric} onValueChange={(value) => value && setView("metric", value)}>
            <SelectTrigger className="h-8 w-[170px]">
              <SelectValue>
                {(value) =>
                  CRYOD_METRICS.find((entry) => entry.value === value)?.label ?? String(value)
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[12rem]">
              {CRYOD_METRICS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <PillGroup options={CRYOD_AGGS} value={agg} onChange={(value) => setView("agg", value)} />
        <PillGroup
          options={CRYOD_WINDOWS}
          value={window}
          onChange={(value) => setView("window", value)}
        />
      </div>

      {/* KPI tiles */}
      <Kpis summary={summary} />

      {/* Nested re-pivot */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Break down by
          </span>
          <Select value={breakdown} onValueChange={(value) => value && setView("breakdown", value)}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue>{(value) => cryodDimensionLabel(String(value))}</SelectValue>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[12rem]">
              {breakdownOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <CryodPivotTable
          rows={overviewQuery.data ?? []}
          isLoading={overviewQuery.isLoading}
          groupBy={breakdown}
          subGroupBy=""
          metricLabel={metricLabel}
          metricUnit={unit}
          onDrill={handleDrill}
        />
      </div>

      {/* Recent runs */}
      <div className="space-y-2">
        <h2 className="text-sm font-medium">Recent runs</h2>
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Test</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">{metricLabel}</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(runsQuery.data ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-sm text-muted-foreground">
                    {runsQuery.isLoading ? "Loading…" : "No runs in this slice."}
                  </TableCell>
                </TableRow>
              ) : (
                (runsQuery.data ?? []).map((run, index) => (
                  <TableRow key={`${run.run_id}-${run.test_name}-${index}`}>
                    <TableCell>
                      <Link
                        to={`/ci/${encodeURIComponent(run.run_id)}`}
                        className="font-mono text-xs text-primary hover:underline"
                      >
                        {run.run_id}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{run.test_name}</TableCell>
                    <TableCell>
                      <CiStatusBadge status={run.status} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      {formatMetricValue(run.value, unit)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {formatTimestamp(run.timestamp)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
